import React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { IParticipationSummary } from "@Interfaces";
import {
    getParticipationDataPhase,
    participationIconChipSx,
    PARTICIPATION_BAR_FILL,
    PARTICIPATION_BAR_FILL_LEADER,
    PARTICIPATION_BAR_TRACK,
    PARTICIPATION_BODY_TEXT,
    PARTICIPATION_FONT_FAMILY,
    PARTICIPATION_ICON_SIZE,
    PARTICIPATION_ICON_STROKE,
    PARTICIPATION_INDIGO,
    PARTICIPATION_INDIGO_TINT,
    PARTICIPATION_INSET_BG,
    PARTICIPATION_MUTED_TEXT,
    PARTICIPATION_MUTED_VALUE,
    PARTICIPATION_ROW_HEIGHT,
    PARTICIPATION_TEXT_LG,
    PARTICIPATION_TEXT_SM,
    PARTICIPATION_TEXT_XS,
    PARTICIPATION_TITLE_SIZE,
} from "./participationStyles";

interface HighestEngagementQuestionProps {
    summary: IParticipationSummary;
}

export const HighestEngagementQuestion: React.FC<
    HighestEngagementQuestionProps
> = ({ summary }) => {
    const { t } = useTranslation();
    const phase = getParticipationDataPhase(summary.respondents);
    const topQuestion = summary.top_question;
    const hasVotes = phase !== "none" && !!topQuestion;

    const answeredCount = (() => {
        if (!topQuestion) return 0;
        const matching = summary.answers_per_question.find(
            (q) => q.question_id === topQuestion.question_id
        );
        return (
            matching?.answered_count ??
            topQuestion.options.reduce((sum, o) => sum + o.count, 0)
        );
    })();

    const topTotal =
        topQuestion?.options.reduce((sum, o) => sum + o.count, 0) || 1;
    const topMax = topQuestion
        ? Math.max(...topQuestion.options.map((o) => o.count))
        : 0;

    return (
        <Box
            sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E8EBF0",
                borderRadius: "12px",
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                fontFamily: PARTICIPATION_FONT_FAMILY,
                color: PARTICIPATION_BODY_TEXT,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box sx={participationIconChipSx}>
                    <svg
                        width={PARTICIPATION_ICON_SIZE}
                        height={PARTICIPATION_ICON_SIZE}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={PARTICIPATION_ICON_STROKE}
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M8 4h8v5a4 4 0 0 1-8 0z" />
                        <path d="M8 6H5v1a3 3 0 0 0 3 3" />
                        <path d="M16 6h3v1a3 3 0 0 1-3 3" />
                        <path d="M12 13v4" />
                        <path d="M9 20h6" />
                    </svg>
                </Box>
                <Box
                    component="span"
                    sx={{ fontSize: PARTICIPATION_TITLE_SIZE, fontWeight: 700 }}
                >
                    {t("pages.participation.widgets.highestEngagement.title")}
                </Box>
                <Tooltip
                    title={t(
                        "pages.participation.widgets.highestEngagement.info"
                    )}
                >
                    <svg
                        width={PARTICIPATION_ICON_SIZE}
                        height={PARTICIPATION_ICON_SIZE}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#C3C8D4"
                        strokeWidth={2}
                        strokeLinecap="round"
                        role="img"
                        aria-label={t(
                            "pages.participation.widgets.highestEngagement.info"
                        )}
                    >
                        <circle cx="12" cy="12" r="9" />
                        <path d="M12 11v5" />
                        <path d="M12 8h.01" />
                    </svg>
                </Tooltip>
            </Box>

            {hasVotes && topQuestion ? (
                <Box
                    sx={{
                        backgroundColor: PARTICIPATION_INSET_BG,
                        borderRadius: "12px",
                        padding: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "10px",
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                fontSize: PARTICIPATION_TEXT_LG,
                                fontWeight: 700,
                            }}
                        >
                            {topQuestion.question_text}
                        </Box>
                        <Box
                            component="span"
                            sx={{
                                fontSize: PARTICIPATION_TEXT_XS,
                                fontWeight: 700,
                                color: PARTICIPATION_INDIGO,
                                backgroundColor: PARTICIPATION_INDIGO_TINT,
                                padding: "5px 12px",
                                borderRadius: "999px",
                            }}
                        >
                            {phase === "few"
                                ? t(
                                      "pages.participation.widgets.highestEngagement.answeredFew",
                                      { answered: answeredCount }
                                  )
                                : t(
                                      "pages.participation.widgets.highestEngagement.answeredMany",
                                      {
                                          answered: answeredCount,
                                          respondents: summary.respondents,
                                      }
                                  )}
                        </Box>
                    </Box>

                    {topQuestion.options.map((option) => {
                        const pct = (option.count / topTotal) * 100;
                        const width = Math.max(pct, option.count > 0 ? 6 : 0);
                        const isLeader =
                            option.count === topMax && option.count > 0;

                        return (
                            <Box
                                key={option.choice_id ?? option.label}
                                sx={{
                                    position: "relative",
                                    height: PARTICIPATION_ROW_HEIGHT,
                                    borderRadius: "10px",
                                    backgroundColor: PARTICIPATION_BAR_TRACK,
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        borderRadius: "10px",
                                        backgroundColor: isLeader
                                            ? PARTICIPATION_BAR_FILL_LEADER
                                            : PARTICIPATION_BAR_FILL,
                                        width: `${width}%`,
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: "absolute",
                                        inset: 0,
                                        padding: "0 14px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        fontSize: PARTICIPATION_TEXT_SM,
                                    }}
                                >
                                    <Box component="span">
                                        {topQuestion.poll_type === "nps"
                                            ? t(
                                                  `pages.participation.widgets.highestEngagement.nps.${option.label}`
                                              )
                                            : option.label}{" "}
                                        ({option.count})
                                    </Box>
                                    <Box component="b">
                                        {phase === "many"
                                            ? `${pct.toFixed(1)}%`
                                            : ""}
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <Box
                    sx={{
                        backgroundColor: PARTICIPATION_INSET_BG,
                        borderRadius: "12px",
                        padding: "20px 16px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "6px",
                        textAlign: "center",
                    }}
                >
                    <Box
                        sx={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: PARTICIPATION_MUTED_VALUE,
                        }}
                    >
                        –
                    </Box>
                    <Box
                        sx={{
                            fontSize: PARTICIPATION_TEXT_SM,
                            color: PARTICIPATION_MUTED_TEXT,
                        }}
                    >
                        {t(
                            "pages.participation.widgets.highestEngagement.empty"
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};
